import { Box } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { Trade } from "../../types";
import { useTransactions } from "../../hooks/useTransactions";
import { useAtomValue } from "jotai";
import { AccountAtom } from "../../store";
import { track, EVENTS } from "../../utils/analytics";
import StarterKit from "@tiptap/starter-kit";
import { EditorProvider } from "@tiptap/react";
import { MenuBar } from "../../components/texteditor/MenuBar";
import "./Notes.css";
import { TagEntry } from "../../features/notes/TagEntry";
import { useDebounce } from "../../features/hooks";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";

export default function NoteEditor({
  trade,
  menuBarEnabled,
  tagsEnabled,
}: {
  trade: Trade;
  menuBarEnabled?: boolean;
  tagsEnabled?: boolean;
}) {
  const useTransactionsHook = useTransactions();
  const accountState = useAtomValue(AccountAtom);

  const [notes, setNotes] = React.useState<string>("");
  const [initialNoteLength, setInitialNoteLength] = React.useState<number>(0);

  const saveText = (value: string) => {
    // Track if this is a new note or an edit to an existing note
    if (!trade.notes || trade.notes.length === 0) {
      track(EVENTS.NOTES.CREATED, {
        tradeId: trade.id,
        noteLength: value.length,
      });
    } else if (value !== trade.notes) {
      track(EVENTS.NOTES.EDITED, {
        tradeId: trade.id,
        initialLength: initialNoteLength,
        newLength: value.length,
      });
    }

    useTransactionsHook.modifyNote(
      value,
      [trade],
      accountState?.selectedAccount?.id
    );
  };

  const onNoteFieldChange = useDebounce(saveText, 450);

  useEffect(() => {
    const noteContent = trade.notes ? trade.notes : "";
    setNotes(noteContent);
    setInitialNoteLength(noteContent.length);
  }, [trade.notes]);

  // Track note formatting when it contains special elements
  const trackFormatting = (content: string) => {
    if (content.includes("<img")) {
      track(EVENTS.NOTES.IMAGE_ADDED, {
        tradeId: trade.id,
      });
    }

    if (content.includes("<a href=")) {
      track(EVENTS.NOTES.LINK_ADDED, {
        tradeId: trade.id,
      });
    }

    // Track other formatting like headers, lists, etc.
    if (
      content.includes("<h1>") ||
      content.includes("<h2>") ||
      content.includes("<h3>") ||
      content.includes("<h4>") ||
      content.includes("<h5>") ||
      content.includes("<h6>")
    ) {
      track(EVENTS.NOTES.FORMATTING_APPLIED, {
        tradeId: trade.id,
        format: "heading",
      });
    }

    if (content.includes("<ul>") || content.includes("<ol>")) {
      track(EVENTS.NOTES.FORMATTING_APPLIED, {
        tradeId: trade.id,
        format: "list",
      });
    }
  };

  const extensions = [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3, 4, 5, 6],
      },
    }),
    Placeholder.configure({
      placeholder: "Add you notes here...",
    }),
    Image.configure({
      inline: true,
      allowBase64: true,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: "https",
    }),
  ];

  return (
    <EditorProvider
      slotBefore={
        <Box mb="20px">
          {menuBarEnabled ? <MenuBar /> : null}
          {tagsEnabled ? <TagEntry trade={trade} /> : null}
        </Box>
      }
      extensions={extensions}
      content={trade.notes}
      onUpdate={(e) => {
        const newContent = e.editor.getHTML();
        setNotes(newContent);
        onNoteFieldChange(newContent);
        trackFormatting(newContent);
      }}
      editorProps={{
        attributes: {
          class: "prose",
        },
      }}
    ></EditorProvider>
  );
}
